import { FiltersButton, InfoBanner, TableHeader, WithRoleAccess } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ClientsTable from "./ClientsTable";
import { useNavigate, useParams } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import ReactSelect from "@services/ui-components/src/ReactSelect/ReactSelect";
import RequirementsFileInfo from "../../services/importFiles/RequirementsFileInfo";
import { requirementsInfoType } from "../../interfaces/interfaces";
import { ImportFiles } from "../ImportFiles/ImportFiles";
import { ExportBtn } from "../ExportBtn/ExportBtn";
import { EntityType } from "../../utils/enums";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccessAny } from "@me-team/host/src/utils/useRoleAccess";

interface Option {
    value: string;
    label: string;
}

enum VisitRanges {
    RANGE_0 = "0",
    RANGE_1_10 = "1-10",
    RANGE_11_30 = "11-30",
    RANGE_31_50 = "31-50",
    RANGE_50_PLUS = "50+",
}

const Clients: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const hasPageActions = useRoleAccessAny([
        CompanyAccess.ClientExport,
        CompanyAccess.ClientImport,
        CompanyAccess.ClientCreate,
    ]);
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState({ search: "", amountOfVisits: "" });
    const [selectedOption, setSelectedOption] = useState<Option | null>(null);
    const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
    const [clientRequirementsInfo, setClientRequirementsInfo] = useState<requirementsInfoType>(null);

    const toggleFilters = () => setShowFilters(!showFilters);

    const transformedOptions = Object.values(VisitRanges).map((range) => ({
        value: range,
        label: range,
    }));

    const handleSelectChange = (option: Option | null) => setSelectedOption(option);
    const handleApplyFilter = () => {
        const amountOfVisitsValue = selectedOption ? selectedOption?.value : "";
        setFilter({
            amountOfVisits: amountOfVisitsValue,
            search: search,
        });
    };

    useEffect(() => {
        if (currentCompanyId) {
            setClientRequirementsInfo(
                RequirementsFileInfo.clientFileRequirements(parseInt(currentCompanyId), t, i18n.language, branchId)
            );
        }
    }, [i18n.language, currentCompanyId, t, branchId]);

    const onOpenImportModalHandler = () => {
        setIsImportModalOpen(true);
    };
    const onCloseImportModalHandler = () => {
        setIsImportModalOpen(false);
    };

    const handleClearFilter = () => {
        setFilter({ search: "", amountOfVisits: "" });
        setSelectedOption(null);
        setSearch("");
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };

    const handleCreateClient = () => {
        navigate(`/clients/${currentCompanyId}/new/${branchId}`);
    };

    return (
        <div className="mb-9">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "All your Customers are displayed on this page. When creating records, through the record log or online record, new Customers are added to the customer base automatically. You can also create a new client by clicking Add client"
                    )}{" "}
                    <i className="bi bi-people"></i>.
                </p>
            </InfoBanner>

            <TableHeader
                title={t("List of Clients")}
                paths={[
                    { path: `/clients/${currentCompanyId}/list/${branchId}`, label: t("Customers") },
                    { path: `/clients/${currentCompanyId}/list/${branchId}`, label: t("List of Clients") },
                ]}
                colSize={5}
                btnColSize={7}
                buttons={
                    <Row className="gx-3 flex-lg-nowrap justify-content-end mt-3 mt-lg-0">
                        <WithRoleAccess permission={CompanyAccess.ClientExport}>
                            <Col xs={2} xl={3}>
                                <ExportBtn
                                    companyId={parseInt(currentCompanyId)}
                                    exportType={EntityType.CLIENTS}
                                    branchId={branchId}
                                    classes="d-none d-xl-inline-block"
                                />
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.ClientImport}>
                            <Col xs={2} xl={3}>
                                <Button
                                    variant="outline-violet"
                                    className="border-primary text-primary fw-normal d-flex align-items-center justify-content-center w-100"
                                    onClick={onOpenImportModalHandler}
                                >
                                    <i className="bi bi-box-arrow-in-down"></i>
                                    <span className="d-none d-xl-inline-block ms-1">{t("import")}</span>
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.ClientCreate}>
                            <Col xs={5} xl={4}>
                                <Button
                                    variant="primary"
                                    className="w-100 fw-bold text-truncate"
                                    onClick={handleCreateClient}
                                >
                                    <i className="bi bi-plus-lg me-1 w-100 h-100"></i>
                                    {t("Add client")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={hasPageActions ? 3 : 6} xl={hasPageActions ? 2 : 3}>
                            <FiltersButton
                                filter={filter}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classesText={`${hasPageActions ? "d-none d-xxl-inline-block" : "d-inline-block"}`}
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <Row className="gx-3">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col md={6} className="text-primary">
                                <div className="position-relative mb-3 mb-lg-0">
                                    <input
                                        id="listOfClients-search-field"
                                        type="text"
                                        className="form-control text-truncate pe-5"
                                        placeholder={t("Search by name, surname, phone or e-mail")}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                    <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"></i>
                                </div>
                            </Col>

                            <Col md={6} className="text-primary mb-3 mb-lg-0">
                                <ReactSelect
                                    id="listOfClients-byTheNumberOfVisits-field"
                                    placeholder={t("By the number of Visits")}
                                    options={transformedOptions}
                                    value={selectedOption}
                                    onChange={handleSelectChange}
                                    isSearchable={true}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6} className="mb-3 mb-lg-0">
                                <button
                                    type="button"
                                    className="btn btn-outline-dark border-grey w-100 fw-normal"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </button>
                            </Col>
                            <Col xs={6} className="mb-3 mb-lg-0">
                                <button className="btn btn-primary w-100 px-1" onClick={handleApplyFilter}>
                                    {t("apply")}
                                </button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>
            <ClientsTable filter={filter} />
            {clientRequirementsInfo && (
                <ImportFiles
                    currentRequirementsInfo={clientRequirementsInfo}
                    isOpen={isImportModalOpen}
                    onClose={onCloseImportModalHandler}
                />
            )}
        </div>
    );
};

export default Clients;
